import React, { useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { AddCircleOutline, ExpandMore } from "@mui/icons-material";

import { HelpBox } from "@/components/atoms";
import { Variable, SearchResult } from "@/types";
import { getVariableIcon, toIndex } from "@/utils";


interface VariableCategoryAccordionProps<T extends Variable> {
  category: string;
  categoryText?: string;
  description?: string;
  variables: T[];
  score: keyof T;
  onSelect: (variables: T[]) => void;
  onDelete: (variable: T) => void;
  selected: Set<number>;
  active: Set<number>;
}

const VariableCategoryAccordion: React.FC<VariableCategoryAccordionProps<any>> = <T extends Variable>({
  category,
  categoryText,
  description,
  variables,
  score,
  onSelect,
  onDelete,
  selected,
  active
}: VariableCategoryAccordionProps<T>) => {

  const NUM_VARS = 12;
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMore = variables.length > NUM_VARS;
  const visibleVariables = isExpanded ? variables : variables.slice(0, NUM_VARS);

  const handleSelectCategoryVariables = (variables: T[]) => {
    // Isolate the variables that aren't selected already
    const updatedVariables = variables.filter(x => !isActive(x));

    // Add them
    onSelect(updatedVariables);
  }

  const isSelected = (variable: Variable) => {
    return selected.has(variable.tag_id);
  };

  const isActive = (variable: Variable) => {
    return active.has(variable.tag_id);
  }

  return (
    <Accordion
      defaultExpanded
      slotProps={{
        root: {
          elevation: 4
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        // sx={{
        //   '& .MuiAccordionSummary-content': {
        //     margin: '0 !important', // Forces the margin to stay dead flat
        //   },
        // }}
      >
        <Stack spacing={1}>
          <Typography variant="body2">{category}</Typography>
          {categoryText && <Typography variant="body2" color="textSecondary">{categoryText}</Typography>}
        </Stack>
      </AccordionSummary>
      <AccordionDetails >
        <Stack spacing={1}>
          {description && <Typography variant="body2" color="textSecondary">{description}</Typography>}
          <Box>
            {visibleVariables.map((variable) => (
              <HelpBox
                title={
                  isActive(variable) ? "Selected in the active query group"
                    : isSelected(variable) ? "Selected in another query group"
                      : `${variable.tag_type} | ${variable.tag_category} | ${variable.tag_name}`
                }
              >
                <Chip
                  key={variable.tag_id}
                  size="small"
                  color={isActive(variable) ? "secondary" : isSelected(variable) ? "info" : "default"}
                  label={variable.tag_name}
                  icon={getVariableIcon(variable)}
                  onClick={() => !isActive(variable) && onSelect([variable])}
                  onDelete={isActive(variable) ? () => onDelete(variable) : undefined}
                  sx={{ m: 0.5 }}
                />
              </HelpBox>
            ))}
            {hasMore && (
              <Button
                size="small"
                variant="text"
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0, minWidth: 'auto' }}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </Button>
            )}
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default VariableCategoryAccordion;
